import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { AdminApi } from "../api/client.js";
import { ApiError } from "../api/client.js";
import { Card } from "../components/Card.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { Field } from "../components/Field.js";

const Schema = z.object({
  to: z.string().email("Enter a valid email address"),
});

type FormValues = z.infer<typeof Schema>;

type Props = { api: AdminApi };

export function TestEmailPage({ api }: Props) {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: { to: "" },
  });

  const send = useMutation({
    mutationFn: (values: FormValues) => api.sendTestEmail(values.to),
    onSuccess: (result) => {
      setSentTo(result.to);
      reset({ to: "" });
    },
  });

  const errorMessage =
    send.error instanceof ApiError
      ? send.error.code === "email_not_configured"
        ? "Email is not configured for this environment. Check ACS_CONNECTION_STRING and ACS_SENDER_ADDRESS on the API Container App."
        : send.error.code === "email_send_failed"
          ? send.error.message ||
            "The email provider rejected the message. Check ACS configuration and sender domain verification."
          : send.error.code === "route_not_found" || send.error.status === 404
            ? "The test email API route is not available on this API revision yet. Wait for the API deploy to finish, then try again."
            : send.error.code === "unauthorized"
              ? "Your session could not authorize this request. Sign out and back in, then try again."
              : send.error.code === "internal_error"
                ? "The API returned an unexpected error. If test email keeps failing, ask platform ops to re-sync ACS-CONNECTION-STRING in Key Vault and confirm ACS_SENDER_ADDRESS is noreply@mail.pricare.health."
                : send.error.message
                  ? `${send.error.message} (HTTP ${send.error.status})`
                  : `Request failed (HTTP ${send.error.status})`
      : send.error instanceof Error
        ? send.error.message
        : null;

  return (
    <div className="max-w-xl">
      <Card title="Send test email">
        <p className="mb-4 text-sm text-slate-600">
          Send a sample appointment notification email to verify Azure
          Communication Services is configured correctly.
        </p>

        {errorMessage ? <ErrorBanner message={errorMessage} /> : null}
        {sentTo ? (
          <div
            role="status"
            className="mb-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
          >
            Test email sent to <strong>{sentTo}</strong>.
          </div>
        ) : null}

        <form
          className="grid gap-4"
          onSubmit={handleSubmit((values) => {
            setSentTo(null);
            send.mutate(values);
          })}
        >
          <Field
            label="Recipient email"
            type="email"
            autoComplete="email"
            required
            error={errors.to?.message}
            {...register("to")}
          />
          <div>
            <button
              type="submit"
              disabled={send.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {send.isPending ? "Sending…" : "Send test email"}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
